<template lang="pug">
.metrics-tags
  aiq-tag(
    v-for="metricId in metrics"
    :key="metricId"
    :closable="!disabled"
    type="info"
    @close="onRemove(metricId)"
  ) {{ getMetricLabel(metricId) }}
  .add-metric-dropdown(v-if="!isMaxed && !disabled")
    aiq-select(
      :placeholder="$t('settings_tab.agent_tab.dashboard.pulse_configuration.add_metric')"
      :size="size"
      clearable
      :filterable="selectConfig.filterable"
      :empty-values="selectConfig.emptyValues"
      :value-on-clear="''"
      popper-class="pulse-config-dropdown"
      @change="onAdd"
    )
      aiq-option(
        v-for="metric in selectableMetrics"
        :key="metric.id"
        :label="metric.label"
        :value="metric.id"
      )
  .max-reached(v-else-if="isMaxed") {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.selection_limit_reached') }}
</template>

<script>
import without from 'lodash/without';
import { MAX_METRICS_PER_SECTION } from '@/pages/Pulse/365/constants';

/**
 * The metric tag list shared by every Pulse 360 configuration surface: the
 * org-level "All Segments" picker, each segment's picker, and the per-agent
 * My Pulse 360 picker. Emits the full next selection rather than mutating,
 * so callers stay free to hold their config immutably.
 */
export default {
  name: 'PulseMetricPicker',
  props: {
    metrics: {
      type: Array,
      default: () => [],
    },
    availableMetrics: {
      type: Array,
      required: true,
    },
    maxMetrics: {
      type: Number,
      default: MAX_METRICS_PER_SECTION,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // '' keeps the Settings page on Element Plus's default size; the My Account
    // modal passes 'small' to match its other tabs.
    size: {
      type: String,
      default: '',
    },
  },
  emits: ['update'],
  data() {
    return {
      selectConfig: {
        filterable: true,
        emptyValues: [''],
      },
    };
  },
  computed: {
    isMaxed() {
      return this.metrics.length >= this.maxMetrics;
    },
    selectableMetrics() {
      return this.availableMetrics.filter(metric => !this.metrics.includes(metric.id));
    },
  },
  methods: {
    getMetricLabel(metricId) {
      const metric = this.availableMetrics.find(m => m.id === metricId);
      return metric ? metric.label : metricId;
    },
    onAdd(metricId) {
      if (!metricId || this.metrics.includes(metricId) || this.isMaxed) {
        return;
      }
      this.$emit('update', [...this.metrics, metricId]);
    },
    onRemove(metricId) {
      this.$emit('update', without(this.metrics, metricId));
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../../../styles/aiq-variables.scss";

.metrics-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 10px;
  min-height: 32px;

  ::v-deep .aiq-tag {
    margin: 0;
    background-color: $aiq-color-active;
    border-color: $aiq-color-active;
    color: white;
    height: 38px;
    padding: 0 18px;
    border-radius: 19px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    line-height: normal;
    font-weight: 500;

    &:hover {
      background-color: darken($aiq-color-active, 10%);
      border-color: darken($aiq-color-active, 10%);
    }

    .el-tag__close {
      color: white;
      margin-left: 8px;
      margin-top: 0;
      display: flex;
      align-items: center;
    }
  }
}

.add-metric-dropdown {
  display: inline-block;
  min-width: 180px;
  position: relative;

  ::v-deep {
    .el-input--suffix .el-input__inner {
      padding-right: 30px;
    }
  }
}

.max-reached {
  color: $aiq-color-text-muted;
  font-size: 13px;
  padding: 8px 0;
  font-weight: 500;
}
</style>

<style lang="scss">
@import "../../../../../../styles/aiq-variables-dark.scss";

.el-popper.pulse-config-dropdown .el-select-dropdown__item {
  height: auto;
  line-height: 1.4;
  white-space: normal;
  word-break: normal;
  overflow-wrap: break-word;

  span {
    white-space: normal;
    word-break: normal;
    overflow-wrap: break-word;
  }
}

[data-theme="dark"] .max-reached {
  color: white;
}
</style>
